<template lang="pug">
managementPage(title="Roles and Permissions"
              @fetchData="loadList"
              @openEditor="onShowAddRoleDialog"
              :creatable="showAddRoleButton"
              :creationTooltipContent="$t('settings_tab.admin_tab.access_tab.role.add_role')"
              :searchable="false")

  template(v-slot:body)
    .role-management
      aiq-dialog(v-model="showAddRoleDialog"
                 :title="$t('settings_tab.admin_tab.access_tab.role.add_role')"
                 @close="showAddRoleDialog = false")
        .add-role-form
          label Role Name
          FilteredInput(v-model="newRoleName"
                        size="small"
                        :regex="/[^a-zA-Z-_]/g"
                        max-length="25"
                        placeholder="Enter role Name")
          label Base Role
          aiq-select(v-model="baseRole" placeholder="Select" size="small")
            aiq-option(v-for="role in roleNames"
                       :key="role.name"
                       :label="role.label"
                       :value="role.name")

        template(#footer)
          aiq-button(@click="onCancel") {{ $t('global.cancel') }}
          aiq-button(type="primary" @click="onAddRole") {{ $t('global.save') }}

    aiq-collapse.scrollbar__collapse(accordion)
      aiq-collapse-item(v-for="item in categorizedRows"
                        :title="item.label"
                        :name="item.index"
                        :key="item.label")
        aiq-table.scrollbar__category(:data="item.rows")
          aiq-table-column(label="Resource"
                          prop="role"
                          min-width="250px")
          aiq-table-column(v-for="role in roleNames"
                          :label="role.label"
                          :prop="role.name"
                          :key="role.name")
            template(v-slot:header="scope")
              span {{ role.label }}
              aiq-button(v-if="!defaultSystemRoles.includes(role.name)"
                         size="small"
                         link
                         @click="onDeleteRole(role.name)")
                fa-icon(:icon="['fas', 'trash']")
            template(v-slot:default="scope")
              permission-item(:role="role",
                              :config="$_get(scope.row, role.name, {})"
                              @request="onRequestPermissionUpdate")
</template>

<script>
import * as log from 'loglevel';
import { mapState } from 'vuex';
import get from 'lodash/get';
import has from 'lodash/has';
import upperFirst from 'lodash/upperFirst';
import capitalize from 'lodash/capitalize';
import sortBy from 'lodash/sortBy';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import PermissionItem from './children/PermissionItem.vue';
import { masterRoleKey, uncategorizedCategory, uncategorizedKey, systemRoles } from '@/constants/access';

const generatePList = (resources, resourcesMetadata = {}) => {
  const permissions = [];
  for (const resource of Object.keys(resources)) {
    if (!get(resourcesMetadata[resource], 'hidden')) {
      for (const permission of resources[resource]) {
        permissions.push(`${permission}_${resource}`);
      }
    }
  }
  return permissions;
};

const reducer = (permissionMap, permission) => ({
  ...permissionMap,
  [permission]: true,
});
const reducePListToMap = permissions => generatePList(permissions).reduce(reducer, {});

const formatPermissionName = (permission, resource, resourcesMetadata) => {
  if (has(resourcesMetadata[resource], 'label')) {
    // Only roles have multiple entry so print permission for it with label.
    const label = get(resourcesMetadata[resource], 'label');
    if (resource.includes('/agents/:agent/roles/')) {
      return `${capitalize(label)}(${capitalize(permission)})`;
    }
    return label;
  }
  return `${capitalize(permission)} ${capitalize(resource)}`;
};

const categoryForResource = (permission, resources) => {
  const config = resources[permission];
  return config ? config.category : uncategorizedKey;
};

export default {
  name: 'manageRoles',
  components: {
    ManagementPage,
    PermissionItem,
  },
  computed: {
    ...mapState({
      roles: state => state.settings.access.roles,
      categoriesMetadata: state => state.settings.access.metadata.categories,
      resourcesMetadata: state => state.settings.access.metadata.resources,
      agentPermissions: state => reducePListToMap(state.agent.permissions),
      myRoles: state => state.agent.profile.roles,
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
      updatableAgentRoles: state => state.agent.updatableAgentRoles,
    }),
    roleNames: function() {
      if (!this.updatableAgentRoles) {
        return [];
      }
      return this.updatableAgentRoles
        .filter(role => role !== masterRoleKey) // Exclude master role
        .map(role => ({
          name: role,
          label: upperFirst(role),
          hidden: role === masterRoleKey,
        }))
        // sort system roles first, then alphabetically
        .sort((a, b) => {
          if (this.defaultSystemRoles.includes(a.name) && !this.defaultSystemRoles.includes(b.name)) {
            return -1; // a is a system role, b is not
          }
          if (!this.defaultSystemRoles.includes(a.name) && this.defaultSystemRoles.includes(b.name)) {
            return 1; // b is a system role, a is not
          }

          // both are either system roles or not, sort alphabetically
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;

          return 0; // both are either system roles or not
        });
    },
    defaultSystemRoles() {
      return systemRoles;
    },
    categorizedRows() {
      if (!this.resourcesMetadata || !this.categoriesMetadata || !this.updatableAgentRoles) {
        return [];
      }

      const masterRole = this.roles.find(role => role.role === masterRoleKey);

      if (!masterRole) {
        return [];
      }

      const permissions = generatePList(masterRole.resources, this.resourcesMetadata, this.updatedAgentRoles);

      /**
      * rows example:
      *  [
      *    {
      *      id: "edit_/resource",
      *      superadmin: {
      *        resource: "/resource",
      *        permission: "edit"
      *        allow: true,
      *        disabled: false
      *      },
      *      admin: {
      *        resource: "/resource",
      *        permission: "edit"
      *        allow: true,
      *        disabled: true
      *      }
      *    }
      *  ]
      */

      const categorizedRowsMap = Object.keys(this.categoriesMetadata).reduce((map, key) => {
        const { index, label } = this.categoriesMetadata[key];
        map[key] = {
          index,
          label: this.handleLabelTranslation(label),
          rows: [],
        };

        return map;
      }, {});

      categorizedRowsMap[uncategorizedKey] = {
        ...uncategorizedCategory,
        rows: [],
      };

      permissions.forEach(perm => {
        const permissionToFormat = perm.split('_');
        const permission = permissionToFormat.shift();
        const resource = permissionToFormat.join('_');

        const row = {
          id: perm,
          role: this.handleLabelTranslation(formatPermissionName(permission, resource, this.resourcesMetadata)),
        };

        this.roles.forEach(item => {
          const itemResource = item.resources[resource];
          row[item.role] = {
            resource,
            permission,
            role: item.role,
            allow: Boolean(itemResource && itemResource.includes(permission)),
            disabled: !this.agentPermissions[perm],
          };
        });

        const category = categoryForResource(resource, this.resourcesMetadata);
        categorizedRowsMap[category].rows.push(row);
      });

      for (const [key, categorized] of Object.entries(categorizedRowsMap)) {
        if (key !== uncategorizedKey) {
          categorized.rows = sortBy(categorized.rows, 'role');
        }
      }

      const filteredMap = Object.values(categorizedRowsMap)
        .filter(item => {
          const hasSuperAdminAccess = item.superadmin ? this.myRoles.includes('superadmin') : true;
          return hasSuperAdminAccess && item?.rows?.length;
        });
      return sortBy(filteredMap, 'index');
    },
    showAddRoleButton() {
      return this.featureFlags.CUSTOM_ROLES && this.updatableAgentRoles.includes(masterRoleKey);
    },
  },
  data() {
    return {
      showAddRoleDialog: false,
      newRoleName: '',
      baseRole: '',
    };
  },
  created() {
    this.$_get = (object, key, defaultValue) => get(object, key, defaultValue);
  },
  methods: {
    loadList() {
      Promise.all([
        this.$store.dispatch('settings/getRolesList'), // Deprecating
        this.$store.dispatch('settings/getRolesMetadata'),
        this.$store.dispatch('agent/getUpdatableAgentRoles'),
      ]);
    },
    handleLabelTranslation(label) {
    // This function localizes the Member experience by transforming labels from the backend
    // into corresponding keys in our translation file. If a matching key is not found,
    // the original backend label is returned as a fallback.
      const translationKey = `settings_tab.admin_tab.access_tab.${label.toLowerCase().replaceAll(' > ', '_').replaceAll(' - ', '_').replaceAll(' ', '_')}`;
      return this.$t(translationKey, label);
    },
    async onRequestPermissionUpdate(payload) {
      try {
        await this.$store.dispatch('settings/updateRole', payload);
        this.$aiq.notify.success(`${capitalize(payload.role)} role's ${payload.config.resource_name} updated.`);
        await this.onPermissionUpdated(payload);
      } catch (err) {
        log.error(err);
        this.$aiq.notify.error(get(err, 'data.error') || 'Edit failed.');
      }
    },
    async onPermissionUpdated({ role, config }) {
      const { resource_name, allow } = config;

      // Update down stream permissions recursively
      const currentResource = this.resourcesMetadata[resource_name];
      const resourcesToUpdate = [];
      for (const [depedentResource, dependentPermissions] of Object.entries(get(currentResource, 'dependencies', {}))) {
        for (const dependentPermission of dependentPermissions) {
          resourcesToUpdate.push(this.onRequestPermissionUpdate({
            role,
            config: {
              resource_name: depedentResource,
              permission: dependentPermission,
              allow,
            },
          }));
        }
      }
      return Promise.all(resourcesToUpdate);
    },
    onShowAddRoleDialog() {
      this.showAddRoleDialog = true;
    },
    onCancel() {
      this.showAddRoleDialog = false;
      this.newRoleName = '';
      this.baseRole = '';
    },
    async onAddRole() {
      if (!this.newRoleName || !this.baseRole) {
        this.$aiq.notify.error('Please enter a valid role name and select a base role.');
        return;
      }

      const payload = {
        role_name: this.newRoleName,
        base_role: this.baseRole,
      };

      try {
        await this.$store.dispatch('settings/addRole', payload);
        this.$aiq.notify.success(`New role ${this.newRoleName} added successfully.`);
        this.onCancel();
        this.loadList();
      } catch (err) {
        log.error(err);
        this.$aiq.notify.error(get(err, 'response.data.error') || 'Failed to add new role.');
      }
    },
    async onDeleteRole(roleName) {
      // confirm deletion
      let confirmDelete = null;

      try {
        confirmDelete = await this.$aiq.confirm({
          title: this.$t('settings_tab.admin_tab.access_tab.role.delete_role'),
          message: this.$t('settings_tab.admin_tab.access_tab.role.delete_confirmation', { roleName }),
          confirmButtonText: this.$t('global.delete'),
          cancelButtonText: this.$t('global.cancel'),
        });

        if (!confirmDelete) {
          return;
        }

        try {
          await this.$store.dispatch('settings/deleteRole', roleName);
          this.$aiq.notify.success(`Role ${roleName} deleted successfully.`);
          this.loadList();
        } catch (err) {
          log.error(err);
          this.$aiq.notify.error(get(err, 'response.data.error') || 'Failed to delete role.');
        }
      } catch (err) {
        log.debug('Role deletion cancelled by user.');
      }
      return;
    },
  },
};

</script>

<style lang="scss" scoped>
  @import "../../../../../styles/aiq-mixins.scss";

  .scrollbar__collapse {
    @include scrollable(calc(100vh - 204px));
  }

  .add-button {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 5px;
  }

  .add-role-form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
</style>

<style lang="scss">
  @import "../../../../../styles/aiq-mixins.scss";

  .scrollbar__category {
    .el-table__body-wrapper {
      @include scrollable(calc(100vh - 210px));
    }
  }
</style>
